import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import { unsubscribe as unsubscribePush } from '../lib/push';
import * as realtime from '../lib/realtime';

export const AuthContext = createContext();

// Synchronous hydration from localStorage. Without this, the first
// paint runs with user=null, then a useEffect hydrates user, then
// downstream contexts (AdminModeContext) flip their state a frame
// later — manifested as the admin banner flashing in over 2-3 frames
// after every page load. Reading storage costs ~1ms; the flash was
// strictly worse than the cost.
const hydrateFromStorage = () => {
  try {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (t && u) return { token: t, user: JSON.parse(u), loading: true };
  } catch {
    // Corrupt JSON / disabled storage — treat as logged out and let
    // the user re-authenticate.
  }
  return { token: null, user: null, loading: false };
};

export const AuthProvider = ({ children }) => {
  const initial = hydrateFromStorage();
  const [user, setUser] = useState(initial.user);
  const [token, setToken] = useState(initial.token);
  const [loading, setLoading] = useState(initial.loading);

  useEffect(() => {
    // Refresh from the server when there's a hydrated session — admin /
    // tier / suspension can drift if the user was upgraded, suspended,
    // or promoted while their token was still alive. Skipped entirely
    // when no session: initial.loading was already false.
    if (initial.token && initial.user) {
      authService.me()
        .then(r => {
          localStorage.setItem('user', JSON.stringify(r.data));
          setUser(r.data);
        })
        .catch(() => {/* token may be invalid — interceptor handles 401/403 */})
        .finally(() => setLoading(false));
    }
    // Mount-once effect — `initial` is a render-time snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the realtime stream whenever we have a token, close it when
  // we don't. The lib itself is idempotent on repeat calls.
  useEffect(() => {
    if (token) realtime.connect(token);
    else realtime.disconnect();
  }, [token]);

  // Cross-tab sign-out broadcast (P1-1).
  // The `storage` event fires in OTHER tabs/windows of the same origin
  // when localStorage changes. If the user signs out (or has their
  // token cleared by the axios 401 interceptor) in tab B, every other
  // tab that has the AuthProvider mounted gets the event and clears
  // its in-memory state. Without this, tab A keeps rendering as
  // logged-in until its next API call — confusing UX in shared-machine
  // scenarios. The event doesn't fire in the originating tab itself,
  // so the logout()/login() flows in this same component still need to
  // call setUser/setToken explicitly.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token') return;
      if (!e.newValue) {
        // Token was cleared in another tab — clear our state too.
        setToken(null);
        setUser(null);
      } else if (e.newValue !== token) {
        // Token was set/changed in another tab — pick up the new
        // value so this tab stays in sync (e.g. user logged into
        // a different account in the other tab).
        setToken(e.newValue);
        const savedUser = localStorage.getItem('user');
        if (savedUser) setUser(JSON.parse(savedUser));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);

  const login = async (email, password, captchaToken = null) => {
    const response = await authService.login(email, password, captchaToken);
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    return response.data;
  };

  const register = async (data) => {
    const response = await authService.register(data);
    const { token, user, status } = response.data;
    // New self-service signups land in 'pending' (v3_35) — no JWT
    // returned, no auto-login. Caller (Register.js) reads `status`
    // and routes to /account-pending. Pre-approval there is no token
    // to set, so don't touch localStorage / context state here.
    if (status === 'pending') {
      return response.data;
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    return response.data;
  };

  const logout = () => {
    // Best-effort: detach push subscription so the next account on
    // this browser doesn't receive notifications meant for the
    // previous one. Fire-and-forget — never block sign-out.
    unsubscribePush().catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Local-only flip after the verify-email API call succeeds. Avoids an
  // extra /me round-trip just to refresh a single boolean.
  const markEmailVerified = () => {
    setUser((u) => {
      if (!u) return u;
      const next = { ...u, emailVerified: true };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, markEmailVerified }}>
      {children}
    </AuthContext.Provider>
  );
};
